import { createRouter, createWebHistory } from 'vue-router'
import { useConfigStore } from '@/stores/config'
import { useUserStore } from '@/modules/user/stores/useUserStore'
import { isAuthMocked } from '@/lib/env'
import { setSentryUser, captureClerkLoadTimeout } from '@/lib/sentry'
import { identifyPosthogUser } from '@/lib/posthog'
import { useVersionChecker } from '@/lib/version'
import type { ClientFeatures } from '@/types/client-config'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'

declare module 'vue-router' {
  interface RouteMeta {
    featureKey?: keyof ClientFeatures
    title?: string
    public?: boolean
    requiresAdmin?: boolean
  }
}

export const FEATURE_ROUTES: { featureKey: keyof ClientFeatures; name: string }[] = [
  { featureKey: 'overview', name: 'overview' },
  { featureKey: 'previousTests', name: 'sessions' },
  { featureKey: 'performance', name: 'performance' },
  { featureKey: 'settings', name: 'settings' },
  { featureKey: 'highlights', name: 'notes' },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/sign-in',
      component: AuthLayout,
      children: [
        {
          path: '',
          name: 'sign-in',
          component: () => import('@/views/SignInView.vue'),
          meta: { title: 'Sign In', public: true },
        },
      ],
    },
    {
      path: '/',
      component: DefaultLayout,
      children: [
        {
          path: '',
          redirect: () => {
            const configStore = useConfigStore()
            const firstEnabled = FEATURE_ROUTES.find((r) => configStore.isFeatureEnabled(r.featureKey))
            return firstEnabled ? { name: firstEnabled.name } : { name: 'sessions' }
          },
        },
        {
          path: 'overview',
          name: 'overview',
          component: () => import('@/views/OverviewView.vue'),
          meta: { featureKey: 'overview', title: 'Overview' },
        },
        {
          path: 'sessions',
          name: 'sessions',
          component: () => import('@/views/SessionDashboardView.vue'),
          meta: { featureKey: 'previousTests', title: 'Sessions' },
        },
        {
          path: 'performance',
          name: 'performance',
          component: () => import('@/views/PerformanceView.vue'),
          meta: { featureKey: 'performance', title: 'Performance' },
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import('@/views/SettingsView.vue'),
          meta: { featureKey: 'settings', title: 'Settings' },
        },
        {
          path: 'notes',
          name: 'notes',
          component: () => import('@/modules/notes/views/NotesView.vue'),
          meta: { featureKey: 'highlights', title: 'Notes' },
        },
        {
          path: 'admin',
          redirect: { name: 'admin-questions' },
          meta: { requiresAdmin: true },
        },
        {
          path: 'admin/questions/:id?',
          name: 'admin-questions',
          component: () => import('@/modules/admin/views/AdminQuestionsView.vue'),
          meta: { requiresAdmin: true, title: 'Admin: Questions' },
        },
      ],
    },
    {
      path: '/session/active',
      name: 'session',
      component: () => import('@/views/ActiveSessionView.vue'),
      meta: { title: 'Active Session' },
    },
    {
      path: '/review/:id',
      name: 'review',
      component: () => import('@/views/ReviewSessionView.vue'),
      meta: { title: 'Review' },
    },
  ],
})

function waitForClerk(timeoutMs = 10_000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.Clerk?.loaded) { resolve(); return }
    const deadline = setTimeout(() => { clearInterval(poll); reject(new Error('clerk_load_timeout')) }, timeoutMs)
    const poll = setInterval(() => {
      if (window.Clerk?.loaded) {
        clearTimeout(deadline)
        clearInterval(poll)
        resolve()
      }
    }, 50)
  })
}

router.beforeEach(async (to) => {
  if (isAuthMocked) {
    // Skip Clerk auth check in local mock mode; still apply feature-flag guard
    if (to.meta.requiresAdmin) return true  // mock mode auto-grants admin
    const featureKey = to.meta.featureKey
    if (!featureKey) return true
    const configStore = useConfigStore()
    if (!configStore.isFeatureEnabled(featureKey)) {
      const firstEnabled = FEATURE_ROUTES.find((r) => configStore.isFeatureEnabled(r.featureKey))
      return firstEnabled ? { name: firstEnabled.name } : false
    }
    return true
  }

  try {
    await waitForClerk()
  } catch {
    captureClerkLoadTimeout(String(to.name ?? to.path))
    if (to.meta.public) return true
    return { name: 'sign-in', query: { error: 'auth_unavailable' } }
  }

  const clerk = window.Clerk!

  if (!to.meta.public && !clerk.user) {
    setSentryUser(null)
    return { name: 'sign-in' }
  }

  if (to.name === 'sign-in' && clerk.user) {
    return { name: 'sessions' }
  }

  // Fetch auth-gated data once after Clerk confirms a valid session
  if (clerk.user) {
    setSentryUser(clerk.user.id)
    identifyPosthogUser(clerk.user.id)
    const configStore = useConfigStore()
    const userStore = useUserStore()
    if (configStore.seasons.length === 0) {
      configStore.fetchSeasons()
    }
    if (!userStore.userInfo && !userStore.isLoading) {
      userStore.initialize(clerk.user.id)
    }
  }

  if (to.meta.requiresAdmin) {
    const role = (clerk.user?.publicMetadata as Record<string, unknown>)?.role
    if (role !== 'admin') return { path: '/' }
    return true
  }

  // Feature-flag guard
  const featureKey = to.meta.featureKey
  if (!featureKey) return true
  const configStore = useConfigStore()
  if (!configStore.isFeatureEnabled(featureKey)) {
    const firstEnabled = FEATURE_ROUTES.find((r) => configStore.isFeatureEnabled(r.featureKey))
    return firstEnabled ? { name: firstEnabled.name } : false
  }
  return true
})

const versionChecker = useVersionChecker()

router.afterEach((to) => {
  try {
    const configStore = useConfigStore()
    const appName = configStore.branding.appName
    document.title = to.meta.title ? `${to.meta.title} - ${appName}` : appName
  } catch {
    // config store not yet initialized
  }
  versionChecker.onRouteChange(to)
})

router.onError((error) => {
  const msg = error?.message ?? ''
  if (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('ChunkLoadError')
  ) {
    versionChecker.onChunkError()
  }
})

export default router
